import {useCallback, useEffect, useMemo, useState} from 'react'
import {BASE_URL} from '../../../api'
import config from '../../config'
import {aiDraft, aiError} from '../_shared/aiDraft'
import HomeBannerCard from '../_shared/HomeBannerCard'
const {axios} = config

// Roadmap #21 (2026-09-28): guided meditations / wellness videos shown in
// the app. YouTube-hosted - paste a link. Spanish is auto-translated when
// left blank.
const emptyNew = {youtube: '', title: '', description: '', section: ''}

const Row = ({v, index, total, onMove, reload, setError, sections}) => {
  const [edit, setEdit] = useState(false)
  const [form, setForm] = useState({})
  const start = () => {
    setForm({title: v.title, titleEs: v.titleEs, description: v.description, descriptionEs: v.descriptionEs, section: v.section, youtube: v.youtubeId})
    setEdit(true)
  }
  const save = async () => {
    try {
      const body = {...form}
      if (body.section === v.section) delete body.section
      if (body.youtube === v.youtubeId) delete body.youtube
      await axios.put(`${BASE_URL}/admin/videos/${v._id}`, body)
      setEdit(false)
      reload()
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not save.')
    }
  }
  const toggle = async () => {
    try {
      await axios.put(`${BASE_URL}/admin/videos/${v._id}`, {hidden: !v.hidden})
      reload()
    } catch (e) {
      setError('Could not update.')
    }
  }
  const remove = async () => {
    if (!window.confirm(`Delete "${v.title}"?`)) return
    await axios.delete(`${BASE_URL}/admin/videos/${v._id}`)
    reload()
  }
  return (
    <tr className={v.hidden ? 'opacity-50' : ''}>
      <td className='text-nowrap'>
        <button className='btn btn-sm btn-icon btn-light me-1' disabled={index === 0} onClick={() => onMove(index, -1)}>↑</button>
        <button className='btn btn-sm btn-icon btn-light' disabled={index === total - 1} onClick={() => onMove(index, 1)}>↓</button>
      </td>
      <td style={{width: 130}}>
        <a href={`https://www.youtube.com/watch?v=${v.youtubeId}`} target='_blank' rel='noreferrer'>
          <img src={v.thumbnail} alt='' className='rounded' style={{width: 120, height: 68, objectFit: 'cover'}} />
        </a>
      </td>
      {edit ? (
        <td colSpan={2}>
          <div className='row g-2'>
            <div className='col-md-6'>
              <input className='form-control form-control-sm form-control-solid mb-2' value={form.title} onChange={(e) => setForm({...form, title: e.target.value})} />
              <textarea className='form-control form-control-sm form-control-solid mb-2' rows={3} value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} />
              <input className='form-control form-control-sm form-control-solid mb-2' list='video-sections' value={form.section} onChange={(e) => setForm({...form, section: e.target.value})} />
              <input className='form-control form-control-sm form-control-solid' value={form.youtube} placeholder='YouTube link or id' onChange={(e) => setForm({...form, youtube: e.target.value})} />
            </div>
            <div className='col-md-6'>
              <input className='form-control form-control-sm form-control-solid mb-2' placeholder='Título (auto)' value={form.titleEs} onChange={(e) => setForm({...form, titleEs: e.target.value})} />
              <textarea className='form-control form-control-sm form-control-solid' rows={3} placeholder='Descripción (auto)' value={form.descriptionEs} onChange={(e) => setForm({...form, descriptionEs: e.target.value})} />
            </div>
          </div>
        </td>
      ) : (
        <>
          <td>
            <div className='fw-bold'>{v.title}</div>
            <div className='text-muted fs-7'>{v.titleEs}</div>
            <div className='fs-7 mt-1' style={{maxWidth: 520}}>{v.description}</div>
          </td>
          <td className='text-muted fs-7'>{v.section}</td>
        </>
      )}
      <td className='text-end text-nowrap'>
        {edit ? (
          <>
            <button className='btn btn-sm btn-primary me-2' onClick={save}>Save</button>
            <button className='btn btn-sm btn-light' onClick={() => setEdit(false)}>Cancel</button>
          </>
        ) : (
          <>
            <button className='btn btn-sm btn-light me-2' onClick={start}>Edit</button>
            <button className='btn btn-sm btn-light me-2' onClick={toggle}>{v.hidden ? 'Show' : 'Hide'}</button>
            <button className='btn btn-sm btn-light-danger' onClick={remove}>Delete</button>
          </>
        )}
      </td>
    </tr>
  )
}

const Videos = () => {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [adding, setAdding] = useState(emptyNew)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try {
      const {data} = await axios.get(`${BASE_URL}/admin/videos`)
      setRows(data.data || [])
    } catch (e) {
      setError('Could not load videos.')
    } finally {
      setLoading(false)
    }
  }, [])
  useEffect(() => {
    load()
  }, [load])

  const sections = useMemo(() => [...new Set(rows.map((r) => r.section))], [rows])

  const move = async (i, dir) => {
    const next = [...rows]
    const [x] = next.splice(i, 1)
    next.splice(i + dir, 0, x)
    setRows(next)
    try {
      await axios.put(`${BASE_URL}/admin/videos/order`, {ids: next.map((r) => r._id)})
    } catch (e) {
      setError('Could not save the order.')
      load()
    }
  }

  const add = async () => {
    setError('')
    setBusy(true)
    try {
      await axios.post(`${BASE_URL}/admin/videos`, adding)
      setAdding(emptyNew)
      load()
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not add.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <HomeBannerCard
        settingKey='meditationsBannerUrl'
        endpoint='/admin/videos-banner'
        bannerName='Guided Meditations'
      />
      <datalist id='video-sections'>
        {sections.map((s) => (
          <option key={s} value={s} />
        ))}
      </datalist>
      <div className='card mb-5'>
        <div className='card-body p-9'>
          <h3 className='fw-bolder mb-3'>Guided Meditations</h3>
          <p className='text-muted'>
            Videos in the app's meditation section, grouped by section in this order. Paste any
            YouTube link to add one; leave Spanish blank to auto-translate. Hide takes a video out of
            the app without deleting it.
          </p>
          {error && <div className='alert alert-danger py-3'>{error}</div>}
          <div className='row g-2 align-items-start'>
            <div className='col-md-3'>
              <input className='form-control form-control-solid' placeholder='YouTube link' value={adding.youtube} onChange={(e) => setAdding({...adding, youtube: e.target.value})} />
            </div>
            <div className='col-md-3'>
              <input className='form-control form-control-solid' placeholder='Title' value={adding.title} onChange={(e) => setAdding({...adding, title: e.target.value})} />
            </div>
            <div className='col-md-2'>
              <input className='form-control form-control-solid' list='video-sections' placeholder='Section' value={adding.section} onChange={(e) => setAdding({...adding, section: e.target.value})} />
            </div>
            <div className='col-md-3'>
              <input className='form-control form-control-solid' placeholder='Short description' value={adding.description} onChange={(e) => setAdding({...adding, description: e.target.value, descriptionEs: ''})} />
              <button
                type='button'
                className='btn btn-sm btn-link px-0'
                disabled={!adding.title.trim() && !adding.description.trim()}
                onClick={async () => {
                  setError('')
                  try {
                    const d = await aiDraft('meditation', `${adding.title}. ${adding.description}`)
                    setAdding((a) => ({...a, description: d.description || a.description, descriptionEs: d.descriptionEs || ''}))
                  } catch (e) {
                    setError(aiError(e))
                  }
                }}
              >
                ✨ Write description
              </button>
            </div>
            <div className='col-md-1'>
              <button className='btn btn-primary w-100' disabled={busy || !adding.youtube || !adding.title || !adding.section} onClick={add}>
                {busy ? '…' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className='card mb-5'>
        <div className='card-body p-6'>
          <div className='table-responsive'>
            <table className='table align-middle table-row-dashed fs-6 gy-3'>
              <thead>
                <tr className='text-start text-muted fw-bolder fs-7 text-uppercase'>
                  <th>Order</th>
                  <th></th>
                  <th>Video</th>
                  <th>Section</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {loading && (
                  <tr>
                    <td colSpan={5} className='text-muted'>Loading…</td>
                  </tr>
                )}
                {!loading && !rows.length && (
                  <tr>
                    <td colSpan={5} className='text-muted'>No videos yet.</td>
                  </tr>
                )}
                {rows.map((v, i) => (
                  <Row key={v._id} v={v} index={i} total={rows.length} onMove={move} reload={load} setError={setError} sections={sections} />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </>
  )
}

export default Videos
